import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend
} from 'recharts';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import WorkspaceKpis from '../components/workspace/WorkspaceKpis';
import { useStudentStore } from '../store/StudentStore';
import { useSettings } from '../store/SettingsContext';
import { useDisplaySettings } from '../store/DisplaySettingsContext';
import { VULNERABILITIES } from '../data/mockData';
import { formatDate } from '../utils/dateFormat';

const riskColors = {
  critical: '#ff453a',
  high: '#ff9f0a',
  medium: '#ffd60a',
  low: '#34c759'
};

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { students, incidents, inventoryItems, inventoryController } = useStudentStore();
  const { settings } = useSettings();
  const { settings: displaySettings } = useDisplaySettings();
  const [range, setRange] = useState('30');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });

  const attendanceThreshold = settings.attendanceThreshold || 70;

  const kpis = useMemo(() => {
    const total = students.length;
    const critical = students.filter((student) => student.riskLevel === 'critical').length;
    const attendanceBelow = students.filter((student) => (student.attendance?.rate || 0) < attendanceThreshold).length;
    const openIncidents = incidents.filter((incident) => incident.status === 'open').length;
    return [
      { id: 'total', label: 'Total students', value: total },
      { id: 'critical', label: 'Critical risk', value: critical, badge: { label: 'Critical', variant: 'danger' } },
      { id: 'attendance', label: `Below ${attendanceThreshold}%`, value: attendanceBelow },
      { id: 'incidents', label: 'Open incidents', value: openIncidents }
    ];
  }, [students, incidents, attendanceThreshold]);

  const attendanceTrend = useMemo(() => {
    const weeks = Math.max(
      0,
      ...students.map((student) => student.attendance?.last8Weeks?.length || 0)
    );
    const totals = Array.from({ length: weeks }, () => 0);
    const counts = Array.from({ length: weeks }, () => 0);
    students.forEach((student) => {
      const series = student.attendance?.last8Weeks || [];
      series.forEach((value, index) => {
        totals[index] += value;
        counts[index] += 1;
      });
    });
    const data = totals.map((total, index) => ({
      label: `W${index + 1}`,
      attendance: counts[index] ? Math.round(total / counts[index]) : 0
    }));
    const slice =
      range === '7'
        ? data.slice(-7)
        : range === '30'
          ? data.slice(-8)
          : range === '90'
            ? data
            : data;
    return slice;
  }, [students, range]);

  const attendanceSummary = useMemo(() => {
    if (attendanceTrend.length < 2) return 'Attendance trend not available.';
    const latest = attendanceTrend[attendanceTrend.length - 1].attendance;
    const previous = attendanceTrend[attendanceTrend.length - 2].attendance;
    const delta = latest - previous;
    return `Latest average attendance ${latest}%. ${delta >= 0 ? 'Up' : 'Down'} ${Math.abs(delta)}% from previous period.`;
  }, [attendanceTrend]);

  const vulnerabilityData = useMemo(() => {
    const counts = Object.keys(VULNERABILITIES).reduce((acc, key) => {
      acc[key] = 0;
      return acc;
    }, {});
    students.forEach((student) => {
      student.vulnerabilities?.forEach((item) => {
        if (counts[item] !== undefined) counts[item] += 1;
      });
    });
    return Object.keys(counts)
      .map((key) => ({
        name: VULNERABILITIES[key]?.label || key,
        value: counts[key]
      }))
      .filter((row) => row.value > 0)
      .slice(0, 6);
  }, [students]);

  const riskData = useMemo(() => {
    const counts = { critical: 0, high: 0, medium: 0, low: 0 };
    students.forEach((student) => {
      const level = student.riskLevel || 'low';
      if (counts[level] !== undefined) counts[level] += 1;
    });
    return Object.entries(counts).map(([key, value]) => ({ name: key, value, color: riskColors[key] }));
  }, [students]);

  const alerts = useMemo(() => {
    const overdueInventory = inventoryItems.filter((item) => inventoryController.getDueStatus(item).isOverdue).length;
    const overdueFollowUps = incidents.filter((incident) => {
      if (!incident.followUpRequired || !incident.followUpDate) return false;
      if (incident.status === 'closed') return false;
      return new Date(incident.followUpDate) < new Date();
    }).length;
    return [
      { label: 'Overdue inventory returns', value: overdueInventory },
      { label: 'Overdue incident follow-ups', value: overdueFollowUps }
    ];
  }, [inventoryItems, inventoryController, incidents]);

  const recentIncidents = useMemo(
    () =>
      [...incidents]
        .sort((a, b) => (a.date < b.date ? 1 : -1))
        .slice(0, 5),
    [incidents]
  );

  const reducedMotion = displaySettings.reducedMotion;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Safeguarding Overview</h2>
          <p className="sp-muted">Real-time view of risk, attendance, and incidents.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={() => navigate('/incidents/new')}>
            Log incident
          </Button>
          <Button size="sm" onClick={() => navigate('/students/enrol')}>
            Add student
          </Button>
        </div>
      </div>

      <WorkspaceKpis items={kpis} />

      <Card className="p-4 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Attendance trend</h3>
            <p className="text-xs sp-muted">{attendanceSummary}</p>
          </div>
          <div className="sp-segment" role="tablist" aria-label="Attendance time range">
            {['7', '30', '90', 'custom'].map((value) => (
              <button
                key={value}
                type="button"
                className={range === value ? 'active' : ''}
                onClick={() => setRange(value)}
                role="tab"
                aria-selected={range === value}
              >
                {value === 'custom' ? 'Custom' : `Last ${value}d`}
              </button>
            ))}
          </div>
        </div>
        {range === 'custom' && (
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="sp-label">Start date</label>
              <input
                type="date"
                className="sp-input mt-2 w-full"
                value={customRange.start}
                onChange={(event) => setCustomRange((prev) => ({ ...prev, start: event.target.value }))}
              />
            </div>
            <div>
              <label className="sp-label">End date</label>
              <input
                type="date"
                className="sp-input mt-2 w-full"
                value={customRange.end}
                onChange={(event) => setCustomRange((prev) => ({ ...prev, end: event.target.value }))}
              />
            </div>
            <div className="text-xs sp-muted">
              Custom range uses the available attendance history.
            </div>
          </div>
        )}
        <div className="sp-chart" role="img" aria-label="Line chart showing average attendance over time">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={attendanceTrend}>
              <XAxis dataKey="label" tickLine={false} axisLine={false} />
              <YAxis domain={[0, 100]} tickLine={false} axisLine={false} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="attendance"
                stroke="#0071e3"
                strokeWidth={2}
                dot={false}
                isAnimationActive={!reducedMotion}
                name="Avg attendance %"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-4 space-y-3">
          <div>
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Vulnerability themes</h3>
            <p className="text-xs sp-muted">Top safeguarding themes across current caseload.</p>
          </div>
          <div className="sp-chart" role="img" aria-label="Bar chart showing vulnerability themes">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={vulnerabilityData}>
                <XAxis dataKey="name" tickLine={false} axisLine={false} />
                <YAxis tickLine={false} axisLine={false} />
                <Tooltip />
                <Bar dataKey="value" fill="#0071e3" isAnimationActive={!reducedMotion} name="Students" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="text-xs sp-muted">
            {vulnerabilityData.length ? `${vulnerabilityData[0].name} is the most common theme.` : 'No themes recorded yet.'}
          </div>
        </Card>

        <Card className="p-4 space-y-3">
          <div>
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Risk distribution</h3>
            <p className="text-xs sp-muted">Share of students by risk level.</p>
          </div>
          <div className="sp-chart" role="img" aria-label="Donut chart showing risk distribution">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={riskData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={60}
                  outerRadius={90}
                  isAnimationActive={!reducedMotion}
                >
                  {riskData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="text-xs sp-muted">
            Critical + high risk: {riskData.filter((r) => ['critical', 'high'].includes(r.name)).reduce((sum, r) => sum + r.value, 0)} students.
          </div>
        </Card>

        <Card className="p-4 space-y-3">
          <div>
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Action alerts</h3>
            <p className="text-xs sp-muted">Items needing immediate attention.</p>
          </div>
          <div className="space-y-2">
            {alerts.map((alert) => (
              <div key={alert.label} className="sp-panel sp-panel--muted p-3 flex items-center justify-between">
                <span className="text-sm">{alert.label}</span>
                <Badge variant={alert.value > 0 ? 'danger' : 'inactive'}>{alert.value}</Badge>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-4 space-y-3">
          <h3 className="sp-value" style={{ fontWeight: 600 }}>Recent incidents</h3>
          {recentIncidents.length === 0 ? (
            <p className="text-sm sp-muted">No incidents logged yet.</p>
          ) : (
            <div className="space-y-2 text-sm">
              {recentIncidents.map((incident) => (
                <button
                  key={incident.id}
                  type="button"
                  className="sp-button sp-button--ghost w-full text-left"
                  onClick={() => navigate(`/incidents/${incident.id}`)}
                >
                  {incident.type} · {formatDate(incident.date)} · {incident.severity}
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-4 space-y-3">
          <h3 className="sp-value" style={{ fontWeight: 600 }}>Safeguarding highlights</h3>
          <p className="text-xs sp-muted">Quick links to high-risk students.</p>
          <div className="space-y-2 text-sm">
            {students
              .filter((student) => ['critical', 'high'].includes(student.riskLevel))
              .slice(0, 5)
              .map((student) => (
                <button
                  key={student.id}
                  type="button"
                  className="sp-button sp-button--ghost w-full text-left"
                  onClick={() => navigate(`/students/${student.id}/dashboard`)}
                >
                  {student.name} · {student.riskLevel}
                </button>
              ))}
            {students.filter((student) => ['critical', 'high'].includes(student.riskLevel)).length === 0 && (
              <div className="text-sm sp-muted">No high-risk students recorded.</div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
